import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { isSafeHttpUrl, renderSafeMarkdown } from '../../utils/safeMarkdown';

interface ExhibitionEntry {
  type?: string;
  exhibitionType?: string;
  publicationText?: string;
  // `link` may still exist on records created before the Markdown-link
  // migration. We render it as a legacy fallback only — new records embed
  // links inline as Markdown inside `exhibitionType` / `publicationText`.
  link?: string;
}

interface ExhibitionsPublicationsSectionProps {
  additionalInfoData: Record<string, unknown> | null;
}

const subheadingSx = {
  fontSize: '0.85rem',
  letterSpacing: '0.1em',
  fontWeight: 700,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  mb: 1,
  mt: 1,
};

export const ExhibitionsPublicationsSection: React.FC<ExhibitionsPublicationsSectionProps> = ({
  additionalInfoData,
}) => {
  const entries = (additionalInfoData?.exhibitions as ExhibitionEntry[] | undefined) ?? [];
  const exhibitions = entries.filter(
    (e) =>
      e.type === 'exhibition' &&
      ((e.publicationText ?? '').trim() !== '' ||
        (e.exhibitionType ?? '').trim() !== '' ||
        isSafeHttpUrl(e.link)),
  );
  const publications = entries.filter(
    (e) => e.type === 'publication' && (e.publicationText ?? '').trim() !== '',
  );

  if (exhibitions.length === 0 && publications.length === 0) return null;

  return (
    <Accordion data-testid="exhibitions-publications-section">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h3">Exhibitions &amp; Publications</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {exhibitions.length > 0 && (
            <Box sx={{ mb: publications.length > 0 ? 2 : 0 }}>
              <Typography sx={subheadingSx}>Exhibitions</Typography>
              <Box sx={{ pl: 1 }}>
                {exhibitions.map((exh, idx) => (
                  <Box key={idx} sx={{ mb: 1.5 }}>
                    <Typography variant="body1">
                      {renderSafeMarkdown(exh.publicationText || exh.exhibitionType)}
                      {exh.publicationText && exh.exhibitionType && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' ('}
                          {renderSafeMarkdown(exh.exhibitionType)}
                          {')'}
                        </Typography>
                      )}
                    </Typography>
                    {isSafeHttpUrl(exh.link) && (
                      <Typography
                        variant="body2"
                        color="primary"
                        component="a"
                        href={exh.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View details
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {publications.length > 0 && (
            <Box>
              <Typography sx={subheadingSx}>Publications</Typography>
              <Box sx={{ pl: 1 }}>
                {publications.map((pub, idx) => (
                  <Box key={idx} sx={{ mb: 1.5 }}>
                    <Typography variant="body1">
                      {renderSafeMarkdown(pub.publicationText)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
